import { AuthContext, useApi } from '@repo-level/core-front';
import type { IMissions } from 'api/cms/missions';
import { getMissionsCMS } from 'api/cms/missions';
import type { TMissionsProgressResponse } from 'api/missions';
import { missionsProgress } from 'api/missions';
import { AB_TESTS } from 'modules/abTesting/constants';
import { useABTestingV2 } from 'modules/abTesting/hooks/useABTesting';
import type { FC, ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface IMissionsContext {
  data: IMissions | null;
  pending: boolean;
  error: boolean;
  isShowQuests: boolean;
  isLoadingAbTest: boolean;
  progress: TMissionsProgressResponse | undefined;
  fetchProgress: () => void;
}

export const MissionsContext = createContext<IMissionsContext>(
  {} as IMissionsContext
);

export const MissionsContextProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const {
    i18n: { language },
  } = useTranslation();

  const { user } = useContext(AuthContext);

  const {
    assignABTestVersion,
    isB: isShowQuests,
    isLoading: isLoadingAbTest,
  } = useABTestingV2({
    experiment: AB_TESTS.QUESTS,
  });

  useEffect(() => {
    if (!user) return;
    assignABTestVersion();
  }, [assignABTestVersion, user]);

  const [fetchMissions, { error, pending: pendingCMS, data }] =
    useApi(getMissionsCMS);
  const [fetchProgress, { data: progress, pending: pendingProgress }] =
    useApi(missionsProgress);

  useEffect(() => {
    if (isShowQuests) {
      fetchMissions(language);
    }
  }, [isShowQuests, language, fetchMissions]);

  useEffect(() => {
    if (isShowQuests) {
      fetchProgress();
    }
  }, [isShowQuests, fetchProgress]);

  const value = useMemo(
    () => ({
      data: data || null,
      pending: pendingCMS || pendingProgress,
      error: !!error,
      isShowQuests,
      isLoadingAbTest,
      progress: progress ?? undefined,
      fetchProgress,
    }),
    [
      data,
      pendingCMS,
      pendingProgress,
      error,
      isShowQuests,
      isLoadingAbTest,
      progress,
      fetchProgress,
    ]
  );

  return (
    <MissionsContext.Provider value={value}>
      {children}
    </MissionsContext.Provider>
  );
};
